import React, { useEffect } from 'react';
import { EditFilled } from '@ant-design/icons';
import { Drawer, Flex, Input, Typography, Form, Button } from 'antd';

import classes from './styles.module.css';

import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAccountTitle,
  getCurrentAccount,
} from 'shared/config/store/selectors/account';
import { useChangeAccountTitleMutation } from '../api';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { ApiError } from 'shared/model/types';
import { general, generalFields } from 'shared/model/schemas/rules';
import { RoundContainer } from 'shared/ui';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';

type Props = {
  open: boolean;
  onClose: () => unknown;
};

const AccountNameDrawer: React.FC<Props> = ({ open, onClose }) => {
  const { formatMessage: t } = useIntl();
  const accountTitle = useSelector(getAccountTitle);
  const accountId = useSelector(getCurrentAccount);
  const dispatch = useDispatch();
  const formRules = {
    account: [general(t)['required-field'], ...generalFields(t).account],
  };
  const { showError, showSuccess } = useMessage();
  const [changeAccountTitle] = useChangeAccountTitleMutation();
  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldValue('account', accountTitle);
  }, []);

  const onChangeName = (name: string) => {
    if (!accountId) return;

    dispatch(setActivity(true));
    changeAccountTitle({ accountId, title: name })
      .unwrap()
      .then(() => {
        showSuccess?.(t({ id: 'account.changeName.messages.success' }));
        onClose();
      })
      .catch((err: ApiError) => {
        console.log(err);
        showError?.(t({ id: 'account.changeName.messages.failed' }));
      })
      .finally(() => {
        dispatch(setActivity(false));
      });
  };

  return (
    <Drawer
      title={t({ id: 'account.name.change.title' })}
      open={open}
      closable
      onClose={onClose}
      width={560}>
      <Flex vertical justify="center" align="center" gap={16}>
        <RoundContainer padding={18}>
          <EditFilled style={{ fontSize: 36 }}></EditFilled>
        </RoundContainer>
        <Typography.Text className={classes.text}>
          {t({ id: 'account.name.change.description' })}
        </Typography.Text>

        <Form
          onFinish={(e) => onChangeName(e.account)}
          form={form}
          validateTrigger="onChange"
          style={{
            width: '100%',
            display: 'flex',
            height: '100%',
            flexDirection: 'column',
            gap: 16,
            alignItems: 'center',
            marginTop: 50,
          }}>
          <Form.Item
            style={{ width: '100%', marginBottom: 32 }}
            rules={formRules.account}
            name="account">
            <Input placeholder="Account name" />
          </Form.Item>
          <Button
            id="btn__account-name-submit"
            variant="solid"
            color='default'
            className={classes.button}
            onClick={form.submit}>
            {t({ id: 'buttons.change' })}
          </Button>
        </Form>
      </Flex>
    </Drawer>
  );
};

export default AccountNameDrawer;
